import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  SendOutlined,
  SettingOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PATHS } from "../../../app/router/paths";
import {
  fetchCustomers,
  searchCustomers,
} from "../../../entities/customer/api/customersApi";
import {
  buildCustomerCode,
  getCustomerDisplayName,
} from "../../../entities/customer/model/customerIdentity";
import type { CustomerResponse } from "../../../entities/customer/model/types";
import { fetchProducts } from "../../../entities/product/api/productsApi";
import type { ProductResponse } from "../../../entities/product/model/types";
import { isApiError } from "../../../shared/errors/ApiError";
import { appNotification } from "../../../shared/notifications/appNotification";
import { createLeadTimeTargets } from "../api/productDeadlineApi";
import { DeadlineRuleFields } from "../components/DeadlineRuleFields";
import { buildLeadTimeConfiguration } from "../model/configuration";
import type { LeadTimeConfigurationFormValues } from "../model/types";
import "../styles/ProductDeadline.css";

const { Text, Title } = Typography;
type Step = 0 | 1 | 2;

const STEP_CONFIG = [
  {
    key: "customer",
    icon: <TeamOutlined />,
    title: "対象顧客を選択",
    shortTitle: "顧客",
  },
  {
    key: "product",
    icon: <ShoppingOutlined />,
    title: "対象商品を選択",
    shortTitle: "商品",
  },
  {
    key: "rule",
    icon: <SettingOutlined />,
    title: "ルールを設定",
    shortTitle: "ルール",
  },
];

interface SelectorItem {
  value: string;
  main: string;
  sub: string;
}

interface SelectorListProps {
  items: SelectorItem[];
  selected: string[];
  loading: boolean;
  search: string;
  searchPlaceholder: string;
  emptyText: string;
  onSearch: (value: string) => void;
  onChange: (values: string[]) => void;
  onLoadMore: () => void;
}

const SelectorList = ({
  items,
  selected,
  loading,
  search,
  searchPlaceholder,
  emptyText,
  onSearch,
  onChange,
  onLoadMore,
}: SelectorListProps) => {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allSelected =
    items.length > 0 && items.every((item) => selectedSet.has(item.value));
  const toggleAll = () => {
    if (allSelected) {
      const visible = new Set(items.map((item) => item.value));
      onChange(selected.filter((value) => !visible.has(value)));
    } else {
      onChange(
        Array.from(new Set([...selected, ...items.map((item) => item.value)])),
      );
    }
  };
  return (
    <div className="blp-selector">
      <div className="blp-selector-search">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          size="large"
        />
      </div>
      {loading && items.length === 0 ? (
        <div className="blp-selector-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="blp-selector-toolbar">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {items.length} 件表示
            </Text>
            {items.length > 0 && (
              <button
                type="button"
                className="blp-select-all-btn"
                onClick={toggleAll}
              >
                {allSelected ? "すべて解除" : "すべて選択"}
              </button>
            )}
          </div>
          <div
            className="blp-selector-list"
            onScroll={(event) => {
              const element = event.currentTarget;
              if (
                element.scrollTop + element.clientHeight >=
                element.scrollHeight - 24
              )
                onLoadMore();
            }}
          >
            {items.length === 0 ? (
              <div className="blp-selector-empty">{emptyText}</div>
            ) : (
              items.map((item) => {
                const checked = selectedSet.has(item.value);
                return (
                  <label
                    key={item.value}
                    className={`blp-list-item${checked ? " blp-list-item--checked" : ""}`}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() =>
                        onChange(
                          checked
                            ? selected.filter((value) => value !== item.value)
                            : [...selected, item.value],
                        )
                      }
                    />
                    <span className="blp-list-item-label">
                      <span className="blp-item-label-row">
                        <strong>{item.main}</strong>
                        <span className="blp-item-label-sub">{item.sub}</span>
                      </span>
                    </span>
                  </label>
                );
              })
            )}
            {loading && (
              <div className="blp-selector-loading" style={{ height: 52 }}>
                <Spin size="small" />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SelectedPanel = ({
  items,
  label,
  emptyText,
  onRemove,
}: {
  items: string[];
  label: string;
  emptyText: string;
  onRemove: (value: string) => void;
}) => (
  <div className="blp-selected-panel">
    <div className="blp-selected-panel-header">
      <Text strong style={{ fontSize: 13 }}>
        {label}
      </Text>
      <span className="blp-selected-count">{items.length}</span>
    </div>
    <div className="blp-selected-chips">
      {items.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {emptyText}
        </Text>
      ) : (
        items.map((value) => (
          <Tag
            key={value}
            closable
            onClose={() => onRemove(value)}
            className="blp-chip"
          >
            {value}
          </Tag>
        ))
      )}
    </div>
  </div>
);

const StepIndicator = ({ current }: { current: Step }) => (
  <div className="blp-steps">
    {STEP_CONFIG.map((item, index) => {
      const done = index < current;
      const active = index === current;
      return (
        <span key={item.key} style={{ display: "contents" }}>
          <div
            className={`blp-step${active ? " blp-step--active" : done ? " blp-step--done" : ""}`}
          >
            <div className="blp-step-circle">
              {done ? <CheckOutlined style={{ fontSize: 12 }} /> : index + 1}
            </div>
            <span className="blp-step-label">{item.shortTitle}</span>
          </div>
          {index < STEP_CONFIG.length - 1 && (
            <div
              className={`blp-step-line${done ? " blp-step-line--done" : ""}`}
            />
          )}
        </span>
      );
    })}
  </div>
);

export const ProductDeadlineCreatePage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<LeadTimeConfigurationFormValues>();
  const [step, setStep] = useState<Step>(0);
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [customerPage, setCustomerPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [customerPages, setCustomerPages] = useState(1);
  const [productPages, setProductPages] = useState(1);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingValues, setPendingValues] =
    useState<LeadTimeConfigurationFormValues | null>(null);

  const reportError = useCallback((error: unknown) => {
    appNotification.error({
      message: "選択肢を取得できませんでした",
      description: isApiError(error)
        ? error.message
        : "処理中にエラーが発生しました。",
    });
  }, []);

  const loadCustomers = useCallback(
    async (page: number, query: string, append = false) => {
      setLoading(true);
      try {
        const result = query.trim()
          ? await searchCustomers(query.trim(), true, page, 100)
          : await fetchCustomers(page, 100);
        setCustomers((previous) =>
          append ? [...previous, ...result.items] : result.items,
        );
        setCustomerPage(page);
        setCustomerPages(result.pagination.total_pages);
      } catch (error) {
        reportError(error);
      } finally {
        setLoading(false);
      }
    },
    [reportError],
  );

  const loadProducts = useCallback(
    async (page: number, query: string, append = false) => {
      setLoading(true);
      try {
        const result = await fetchProducts(
          page,
          100,
          query.trim() || undefined,
        );
        setProducts((previous) =>
          append ? [...previous, ...result.items] : result.items,
        );
        setProductPage(page);
        setProductPages(result.pagination.total_pages);
      } catch (error) {
        reportError(error);
      } finally {
        setLoading(false);
      }
    },
    [reportError],
  );

  useEffect(() => {
    const timer = window.setTimeout(
      () => void loadCustomers(1, customerSearch),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [customerSearch, loadCustomers]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => void loadProducts(1, productSearch),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [loadProducts, productSearch]);

  const customerItems = useMemo<SelectorItem[]>(
    () =>
      customers.map((customer) => ({
        value: buildCustomerCode(customer),
        main: customer.branch_code.trim()
          ? `${customer.mt_code.trim()} - ${customer.branch_code.trim()}`
          : customer.mt_code.trim(),
        sub: getCustomerDisplayName(customer),
      })),
    [customers],
  );
  const productItems = useMemo<SelectorItem[]>(
    () =>
      products.map((product) => ({
        value: product.product_code,
        main: product.product_code,
        sub: product.product_name || "商品名なし",
      })),
    [products],
  );
  const targetCount = selectedCustomers.length * selectedProducts.length;

  const submit = async () => {
    if (!pendingValues) return;
    setSaving(true);
    try {
      const result = await createLeadTimeTargets(
        selectedCustomers,
        selectedProducts,
        buildLeadTimeConfiguration(pendingValues),
      );
      setPendingValues(null);
      Modal.success({
        title: `${result.target_count}件の注文期限を設定しました`,
        content: (
          <div style={{ marginTop: 8 }}>
            <div>
              新規 <strong>{result.created_count}</strong> 件、更新{" "}
              <strong>{result.updated_count}</strong> 件
            </div>
            <div style={{ marginTop: 4, color: "#595959", fontSize: 13 }}>
              選択した顧客と商品の注文期限設定を保存しました。
            </div>
          </div>
        ),
        okText: "設定一覧へ戻る",
        onOk: () => navigate(PATHS.PRODUCT_DEADLINE.ROOT),
        icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
      });
    } catch (error) {
      appNotification.error({
        message: "設定できませんでした",
        description: isApiError(error)
          ? error.message
          : "処理中にエラーが発生しました。",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    if (step === 0)
      return (
        <div className="blp-step-layout">
          <div className="blp-step-main">
            <SelectorList
              items={customerItems}
              selected={selectedCustomers}
              loading={loading}
              search={customerSearch}
              searchPlaceholder="顧客コード・会社名で絞り込み"
              emptyText="顧客が見つかりません"
              onSearch={setCustomerSearch}
              onChange={setSelectedCustomers}
              onLoadMore={() => {
                if (!loading && customerPage < customerPages)
                  void loadCustomers(customerPage + 1, customerSearch, true);
              }}
            />
          </div>
          <div className="blp-step-aside">
            <SelectedPanel
              label="選択中の顧客"
              items={selectedCustomers}
              emptyText="まだ選択していません"
              onRemove={(value) =>
                setSelectedCustomers((previous) =>
                  previous.filter((item) => item !== value),
                )
              }
            />
          </div>
        </div>
      );
    if (step === 1)
      return (
        <div className="blp-step-layout">
          <div className="blp-step-main">
            <SelectorList
              items={productItems}
              selected={selectedProducts}
              loading={loading}
              search={productSearch}
              searchPlaceholder="商品コード・商品名で絞り込み"
              emptyText="商品が見つかりません"
              onSearch={setProductSearch}
              onChange={setSelectedProducts}
              onLoadMore={() => {
                if (!loading && productPage < productPages)
                  void loadProducts(productPage + 1, productSearch, true);
              }}
            />
          </div>
          <div className="blp-step-aside">
            <SelectedPanel
              label="選択中の商品"
              items={selectedProducts}
              emptyText="まだ選択していません"
              onRemove={(value) =>
                setSelectedProducts((previous) =>
                  previous.filter((item) => item !== value),
                )
              }
            />
          </div>
        </div>
      );
    return (
      <div className="blp-rule-layout">
        <div className="blp-summary">
          <div className="blp-summary-stat">
            <span className="blp-summary-num">{selectedCustomers.length}</span>
            <span className="blp-summary-label">顧客</span>
          </div>
          <CloseCircleOutlined className="blp-summary-x" />
          <div className="blp-summary-stat">
            <span className="blp-summary-num">{selectedProducts.length}</span>
            <span className="blp-summary-label">商品</span>
          </div>
          <div className="blp-summary-equals">=</div>
          <div className="blp-summary-stat blp-summary-stat--total">
            <span className="blp-summary-num">{targetCount}</span>
            <span className="blp-summary-label">件設定</span>
          </div>
        </div>
        <Collapse
          ghost
          className="blp-review-collapse"
          items={[
            {
              key: "customers",
              label: (
                <span className="blp-collapse-label">
                  <TeamOutlined /> 選択済み顧客を確認{" "}
                  <span className="blp-collapse-badge">
                    {selectedCustomers.length}
                  </span>
                </span>
              ),
              children: (
                <div className="blp-review-chips">
                  {selectedCustomers.map((value) => (
                    <Tag key={value} className="blp-chip">
                      {value}
                    </Tag>
                  ))}
                </div>
              ),
            },
            {
              key: "products",
              label: (
                <span className="blp-collapse-label">
                  <ShoppingOutlined /> 選択済み商品を確認{" "}
                  <span className="blp-collapse-badge">
                    {selectedProducts.length}
                  </span>
                </span>
              ),
              children: (
                <div className="blp-review-chips">
                  {selectedProducts.map((value) => (
                    <Tag key={value} className="blp-chip">
                      {value}
                    </Tag>
                  ))}
                </div>
              ),
            },
          ]}
        />
        <div className="blp-rule-form-wrapper">
          <Title level={5} style={{ marginBottom: 20 }}>
            適用するルール
          </Title>
          <Form
            form={form}
            layout="vertical"
            requiredMark="optional"
            initialValues={{ lead_time_days: 0, cutoff_extra_days: 0 }}
            onFinish={setPendingValues}
          >
            <DeadlineRuleFields form={form} />
          </Form>
        </div>
      </div>
    );
  };

  return (
    <div className="blp-page">
      <StepIndicator current={step} />
      <div className="blp-page-title">
        <span className="blp-page-title-icon">{STEP_CONFIG[step].icon}</span>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {STEP_CONFIG[step].title}
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {step === 0
              ? "ルールを適用したい顧客をすべて選択してください"
              : step === 1
                ? "ルールを適用したい商品をすべて選択してください"
                : `選択した ${selectedCustomers.length} 顧客 × ${selectedProducts.length} 商品に適用するルールを入力してください`}
          </Text>
        </div>
      </div>
      <div className="blp-body">{renderContent()}</div>
      <div className="blp-footer">
        <Button
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            step === 0
              ? navigate(PATHS.PRODUCT_DEADLINE.ROOT)
              : setStep((step - 1) as Step)
          }
        >
          {step === 0 ? "一覧へ戻る" : "前へ"}
        </Button>
        {step < 2 ? (
          <Button
            type="primary"
            size="large"
            disabled={
              step === 0
                ? selectedCustomers.length === 0
                : selectedProducts.length === 0
            }
            icon={<ArrowRightOutlined />}
            iconPosition="end"
            onClick={() => setStep((step + 1) as Step)}
          >
            次へ
            {step === 0 && selectedCustomers.length > 0 && (
              <span className="blp-footer-count">
                {selectedCustomers.length}
              </span>
            )}
            {step === 1 && selectedProducts.length > 0 && (
              <span className="blp-footer-count">
                {selectedProducts.length}
              </span>
            )}
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            icon={<SendOutlined />}
            loading={saving}
            disabled={!targetCount}
            onClick={() => form.submit()}
          >
            {targetCount ? `${targetCount}件を設定する` : "設定する"}
          </Button>
        )}
      </div>
      <ConfirmModal
        values={pendingValues}
        customerCount={selectedCustomers.length}
        productCount={selectedProducts.length}
        saving={saving}
        onCancel={() => setPendingValues(null)}
        onSubmit={() => void submit()}
      />
    </div>
  );
};

const ConfirmModal = ({
  values,
  customerCount,
  productCount,
  saving,
  onCancel,
  onSubmit,
}: {
  values: LeadTimeConfigurationFormValues | null;
  customerCount: number;
  productCount: number;
  saving: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) => {
  if (!values) return null;
  const targetCount = customerCount * productCount;
  const baseDays = Number(values.lead_time_days) || 0;
  const extraDays = Number(values.cutoff_extra_days) || 0;
  const cutoffTime: Dayjs | undefined | null = values.cutoff_time;
  const cutoffStr = cutoffTime ? cutoffTime.format("HH:mm") : null;

  // UI-only preview for parity with develop; this is not the canonical Order calculation.
  const orderDay = dayjs();
  const deliveryNormal = orderDay.add(baseDays, "day");
  const deliveryLate = cutoffStr
    ? orderDay.add(baseDays + extraDays, "day")
    : null;
  const fmt = (date: dayjs.Dayjs) => date.format("M月D日(ddd)");
  const leadLabel = baseDays > 0 ? `${baseDays}日前` : "当日中";
  return (
    <Modal
      open
      centered
      width={520}
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#fa8c16" }} />
          <span>設定内容を確認してください</span>
        </Space>
      }
      okText={`${targetCount}件を設定する`}
      cancelText="戻って修正する"
      okButtonProps={{ loading: saving, type: "primary" }}
      onCancel={onCancel}
      onOk={onSubmit}
      destroyOnHidden
    >
      <div className="blp-confirm-summary">
        <div className="blp-confirm-summary-row">
          <TeamOutlined />
          <strong>{customerCount}</strong> 顧客 &nbsp;×&nbsp;
          <ShoppingOutlined />
          <strong>{productCount}</strong> 商品
          <span className="blp-confirm-summary-eq">
            = <strong>{targetCount}</strong> 件に設定
          </span>
        </div>
      </div>
      <div className="blp-confirm-rule-card">
        <div className="blp-confirm-rule-header">
          <SettingOutlined />
          <span>適用するルール</span>
        </div>
        <div className="blp-confirm-rule-grid">
          <div className="blp-confirm-rule-item">
            <span className="blp-confirm-rule-label">必要リードタイム</span>
            <strong>{leadLabel}</strong>
          </div>
          {cutoffStr && (
            <div className="blp-confirm-rule-item">
              <span className="blp-confirm-rule-label">締切時刻</span>
              <strong>{cutoffStr}</strong>
            </div>
          )}
          {cutoffStr && (
            <div className="blp-confirm-rule-item">
              <span className="blp-confirm-rule-label">締切後の追加日数</span>
              <strong>+{extraDays}日</strong>
            </div>
          )}
          {values.description && (
            <div className="blp-confirm-rule-item blp-confirm-rule-item--full">
              <span className="blp-confirm-rule-label">メモ</span>
              <span>{values.description}</span>
            </div>
          )}
        </div>
        <div className="rule-preview-box rule-preview-box--compact">
          <div className="rule-preview-header">
            <BulbOutlined className="rule-preview-icon" />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#7c3aed" }}>
              このルールの意味
            </span>
          </div>
          <div className="rule-preview-body">
            <div className="rule-preview-desc">
              <p className="rule-preview-desc-line">
                この商品を受け取るには、希望受取日の{" "}
                <strong>{leadLabel}</strong> までに注文が必要です。
              </p>
              {cutoffStr && (
                <p className="rule-preview-desc-line rule-preview-desc-line--warn">
                  注文日の <strong>{cutoffStr}</strong>{" "}
                  を過ぎて注文した場合は、受取可能日がさらに{" "}
                  <strong>{extraDays}日</strong> 後ろへ延びます。
                </p>
              )}
            </div>
            <div className="rule-preview-divider" />
            <div className="rule-preview-example">
              <span className="rule-preview-badge rule-preview-badge--ok">
                例
              </span>
              <span>
                今日（<strong>{fmt(orderDay)}</strong>）
                {cutoffStr && (
                  <>
                    {" "}
                    <span className="rule-preview-sub">
                      {cutoffStr}より前
                    </span>{" "}
                    に
                  </>
                )}
                {!cutoffStr && <>に</>}注文した場合 → 最短{" "}
                <strong className="rule-preview-date">
                  {fmt(deliveryNormal)}
                </strong>{" "}
                に受取可
              </span>
            </div>
            {cutoffStr && deliveryLate && (
              <div className="rule-preview-example rule-preview-example--warn">
                <span className="rule-preview-badge rule-preview-badge--warn">
                  遅
                </span>
                <span>
                  今日 <span className="rule-preview-sub">{cutoffStr}以降</span>{" "}
                  に注文した場合 → +{extraDays}日延長 → 最短{" "}
                  <strong className="rule-preview-date rule-preview-date--warn">
                    {fmt(deliveryLate)}
                  </strong>{" "}
                  に受取可
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
